import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Button, EmptyState } from "../components/ui";
import { formatMoney } from "../lib/format";
import type { ProductOption } from "../api/types";

const SOURCES = [
  ["", "Not set"],
  ["walk_in", "Walk in"],
  ["phone", "Phone"],
  ["whatsapp", "WhatsApp"],
  ["instagram", "Instagram"],
  ["website", "Website"],
  ["referral", "Referral"],
  ["google", "Google"],
  ["existing_client", "Existing client"],
  ["other", "Other"],
] as const;

/**
 * "Add a lead" from a contact: what are they asking about, and when might they
 * come in.
 *
 * The product picker shows the price and duration next to each option, because
 * the point of this screen is setting an expectation -- someone is about to be
 * quoted, and the number has to be the number the catalogue says.
 *
 * The quote is snapshotted server-side at the moment the enquiry is saved. That
 * is why the form never sends a price: if the catalogue changes next month, this
 * lead must still say what they were told today.
 */
export function AddLeadPanel({
  contactId,
  onDone,
}: {
  contactId: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const currency = user?.account?.currency ?? "INR";
  const [productId, setProductId] = useState("");
  const [dealValue, setDealValue] = useState("");
  const [when, setWhen] = useState("");
  const [source, setSource] = useState("");
  const [notes, setNotes] = useState("");

  // The compact picker shape: name, price, duration. No margin -- this is
  // customer-facing copy.
  const options = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.addLead(contactId, {
        product: productId || null,
        // An explicit deal value is saved exactly; blank falls back to the
        // product price on the server.
        quoted_price: dealValue.trim() ? dealValue.trim() : null,
        // `datetime-local` has no zone; the browser's offset is the honest
        // reading of "Tuesday at 10" typed by whoever is at the desk.
        requested_start_at: when ? new Date(when).toISOString() : null,
        source: source || undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads", contactId] });
      void queryClient.invalidateQueries({ queryKey: ["timeline", contactId] });
      setProductId("");
      setDealValue("");
      setWhen("");
      setSource("");
      setNotes("");
      onDone();
    },
  });

  const error = create.error instanceof ApiError ? create.error : null;
  const fields = error?.fields ?? {};

  if (options.data && options.data.length === 0) {
    return (
      <div className="rounded-md bg-slate-50 p-4">
        <EmptyState
          icon={<CalendarClock size={24} />}
          title="Your catalog is empty"
          body="Add what you sell first — then an enquiry can name it, and the price is recorded for you."
        />
        <div className="mt-2 flex justify-center">
          <a
            href="/catalog/products/new"
            className="text-sm font-medium text-slate-900 underline"
          >
            Add a product
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
      className="rounded-lg bg-slate-50 p-4"
    >
      <div className="space-y-3">
        <div>
          <label htmlFor="lead-product" className="block text-xs font-medium text-slate-600">
            What are they asking about?
          </label>
          <select
            id="lead-product"
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            aria-invalid={fields.product?.length ? true : undefined}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          >
            {/* A loading state, because an empty dropdown reads as "you sell
                nothing" rather than "still asking". */}
            <option value="" disabled={options.isPending}>
              {options.isPending ? "Loading your products…" : "Not sure yet"}
            </option>
            {(options.data ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
                {option.category ? ` · ${option.category}` : ""}
              </option>
            ))}
          </select>
          {options.isPending ? (
            <p className="mt-1 text-xs text-slate-500">One moment.</p>
          ) : fields.product?.length ? (
            <p role="alert" className="mt-1 text-xs text-rose-600">
              {fields.product.join(" ")}
            </p>
          ) : dealValue.trim() ? (
            <p className="mt-1 text-xs text-slate-600">
              Your deal value is saved instead of the product price.
            </p>
          ) : (
            <LeadPreview option={(options.data ?? []).find((o) => o.id === productId)} />
          )}
        </div>

        <div>
          <label htmlFor="lead-deal-value" className="block text-xs font-medium text-slate-600">
            Deal value ({currency})
          </label>
          <input
            id="lead-deal-value"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={dealValue}
            onChange={(event) => setDealValue(event.target.value)}
            placeholder="50000"
            aria-invalid={fields.quoted_price?.length ? true : undefined}
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
          {fields.quoted_price?.length ? (
            <p role="alert" className="mt-1 text-xs text-rose-600">
              {fields.quoted_price.join(" ")}
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              The exact amount agreed. Leave blank to use the product price.
            </p>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="lead-when" className="block text-xs font-medium text-slate-600">
              Tentative time
            </label>
            <input
              id="lead-when"
              type="datetime-local"
              value={when}
              onChange={(event) => setWhen(event.target.value)}
              aria-invalid={fields.requested_start_at?.length ? true : undefined}
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            />
            {fields.requested_start_at?.length ? (
              <p role="alert" className="mt-1 text-xs text-rose-600">
                {fields.requested_start_at.join(" ")}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Optional, and nothing is booked. Leave it blank if they have not
                said.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="lead-source" className="block text-xs font-medium text-slate-600">
              How did they ask?
            </label>
            <select
              id="lead-source"
              value={source}
              onChange={(event) => setSource(event.target.value)}
              className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
            >
              {SOURCES.map(([value, text]) => (
                <option key={value} value={value}>
                  {text}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="lead-notes" className="block text-xs font-medium text-slate-600">
            Anything they said
          </label>
          <textarea
            id="lead-notes"
            rows={2}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Wants it before the wedding on the 14th."
            className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Saving…" : "Add enquiry"}
          </Button>
          <Button onClick={onDone} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}

/**
 * Shows what the customer is about to be quoted, before they save.
 *
 * Reads as a quotation, not a form: "600 for 45 minutes" is the sentence the
 * person on the desk will actually say out loud.
 */
function LeadPreview({ option }: { option: ProductOption | undefined }) {
  if (!option) {
    return (
      <p className="mt-1 text-xs text-slate-500">
        Pick something to record the price and duration with it.
      </p>
    );
  }
  return (
    <p className="mt-1 text-xs text-slate-600">
      You would quote{" "}
      <span className="font-medium text-slate-900">
        {formatMoney(option.price)}
      </span>
      {option.effective_duration_minutes
        ? ` for ${formatDuration(option.effective_duration_minutes)}`
        : ""}
      {option.requires_intake_form ? " · needs an intake form first" : ""}
    </p>
  );
}

function formatDuration(minutes: number | null): string {
  if (!minutes) return "";
  if (minutes < 60) return `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours} hour${hours === 1 ? "" : "s"}`;
}
